import type { CourseModule } from '@/types/lesson';

/**
 * Модуль 3: Ветвление и слияние
 */
const module3: CourseModule = {
  id: 'module-3',
  title: 'Ветвление и слияние',
  slug: 'branching-merging',
  order: 3,
  lessons: [
    {
      id: '3-1',
      moduleId: 'module-3',
      title: 'Создание веток',
      slug: 'creating-branches',
      order: 1,
      theory: `# Создание веток

Ветки позволяют работать над разными функциями или экспериментами параллельно.

:::diagram git-flow:::

## История концепции ветвления

### Эволюция подходов к параллельной разработке:

| Система | Подход | Проблема |
|---------|--------|----------|
| CVS | "Sticky tags" | Медленное, сложное |
| Subversion | svn copy (branch) | Дорогостоящая операция |
| **Git** | **Лёгкие ветки** | **Мгновенное создание** |

### Почему ветки в Git особенные?

В традиционных системах ветвление — это копирование всех файлов:
- **CVS/Subversion**: Копирует файлы на сервере → медленно и дорого
- **Git**: Создаёт файл с 40 байтами (хеш коммита) → мгновенно

Это фундаментальное отличие изменило подход к разработке:
- Создавайте ветку для каждой задачи
- Экспериментируйте без страха
- Легко переключайтесь между контекстами

## Что такое ветка?

Ветка — это просто указатель на конкретный коммит. Ветка по умолчанию обычно называется "main" или "master".

### Глубокое погружение: Внутреннее устройство ветки

Ветка технически — это файл в \`.git/refs/heads/\`:

\`\`\`bash
# Посмотреть содержимое файла ветки
cat .git/refs/heads/main
# a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6q7r8s9t0

# Это просто SHA-1 хеш коммита!
# 40 символов = 160 бит
\`\`\`

### Структура хранения веток:

\`\`\`
.git/
├── refs/
│   ├── heads/              # Локальные ветки
│   │   ├── main            # Содержит хеш коммита
│   │   ├── feature-login   # Содержит хеш коммита
│   │   └── bugfix-auth     # Содержит хеш коммита
│   ├── remotes/            # Удалённые ветки
│   │   └── origin/
│   │       ├── main
│   │       └── develop
│   └── tags/               # Теги
│       └── v1.0.0
└── HEAD                    # Указатель на текущую ветку
\`\`\`

### Файл HEAD:

\`\`\`bash
# Посмотреть, куда указывает HEAD
cat .git/HEAD
# ref: refs/heads/main

# При переключении ветки Git просто меняет этот файл!
\`\`\`

## Создание ветки

### Базовые команды:

\`\`\`bash
# Создать новую ветку (без переключения)
git branch feature-name

# Создать и переключиться на неё
git checkout -b feature-name

# Или используя новый синтаксис (Git 2.23+)
git switch -c feature-name
\`\`\`

### Создание ветки от определённого коммита:

\`\`\`bash
# Создать ветку от конкретного коммита
git branch feature-name a1b2c3d

# Создать ветку от тега
git branch hotfix-v1.0 v1.0.0

# Создать ветку от удалённой ветки
git branch feature-name origin/develop
\`\`\`

### Что происходит при создании ветки:

1. Git создаёт файл \`.git/refs/heads/feature-name\`
2. Записывает в него хеш текущего коммита
3. HEAD не меняется (вы остаётесь на текущей ветке)

\`\`\`bash
# До создания ветки
.git/refs/heads/main → a1b2c3d
HEAD → refs/heads/main

# После git branch feature
.git/refs/heads/main → a1b2c3d
.git/refs/heads/feature → a1b2c3d  # Новый файл!
HEAD → refs/heads/main              # Не изменился
\`\`\`

## Список веток

\`\`\`bash
# Список всех локальных веток
git branch

# Список всех веток (включая удалённые)
git branch -a

# Только удалённые ветки
git branch -r

# С информацией о последнем коммите
git branch -v

# С информацией об отслеживании
git branch -vv

# Фильтрация по шаблону
git branch --list "feature/*"
\`\`\`

### Пример вывода:

\`\`\`bash
$ git branch -vv
  develop    a1b2c3d [origin/develop] Добавить функцию входа
* feature    e4f5g6h Добавить валидацию
  main       i7j8k9l [origin/main] Начальный коммит
\`\`\`

Символ \`*\` указывает на текущую ветку (HEAD).

## Переключение веток

\`\`\`bash
# Старый способ
git checkout feature-name

# Новый способ (Git 2.23+)
git switch feature-name

# Создать и переключиться
git checkout -b new-feature
git switch -c new-feature

# Переключиться на предыдущую ветку
git switch -
\`\`\`

### Что происходит при переключении:

1. Git обновляет файл HEAD
2. Обновляет индекс (staging area)
3. Обновляет рабочую директорию

\`\`\`bash
# До переключения
HEAD → refs/heads/main
Рабочая директория → состояние коммита main

# После git switch feature
HEAD → refs/heads/feature
Рабочая директория → состояние коммита feature
\`\`\`

## Удаление ветки

\`\`\`bash
# Удалить слитую ветку (безопасно)
git branch -d feature-name

# Принудительно удалить (неслитую)
git branch -D feature-name

# Удалить удалённую ветку
git push origin --delete feature-name

# Удалить локальную ссылку на удалённую ветку
git branch -d -r origin/feature-name
\`\`\`

### Защита от потери данных:

\`\`\`bash
$ git branch -d feature
error: The branch 'feature' is not fully merged.
If you are sure you want to delete it, run 'git branch -D feature'.

# Git защищает от удаления неслитых веток!
# Используйте -D только если уверены
\`\`\`

## Переименование ветки

\`\`\`bash
# Переименовать текущую ветку
git branch -m new-name

# Переименовать конкретную ветку
git branch -m old-name new-name
\`\`\`

## Практические кейсы

### Кейс 1: "Создать ветку для новой функции"

\`\`\`bash
# Начинаем работу над функцией
git checkout -b feature/user-authentication

# Работаем...
git add .
git commit -m "Добавить форму входа"

# Переключаемся обратно на main
git checkout main

# Создаём ещё одну ветку для багфикса
git checkout -b bugfix/login-error

# Исправляем баг...
git add .
git commit -m "Исправить ошибку валидации"
\`\`\`

### Кейс 2: "Создать ветку от удалённой"

\`\`\`bash
# Получить изменения с сервера
git fetch origin

# Создать локальную ветку, отслеживающую удалённую
git checkout -b feature origin/feature

# Или автоматически (если имена совпадают)
git checkout --track origin/feature
\`\`\`

### Кейс 3: "Массовое создание веток для релиза"

\`\`\`bash
# Создать release ветки от тега
git branch release/v1.0.0 v1.0.0
git branch release/v1.0.1 v1.0.0
git branch release/v1.1.0 v1.0.0

# Посмотреть все release ветки
git branch --list "release/*"
\`\`\`

### Кейс 4: "Восстановить удалённую ветку"

\`\`\`bash
# Если ветка была удалена случайно
git reflog

# Найти хеш последнего коммита ветки
# a1b2c3d HEAD@{5}: checkout: moving from feature to main
# e4f5g6h HEAD@{6}: commit: Последний коммит в feature

# Восстановить ветку
git branch feature e4f5g6h
\`\`\`

## Типичные ошибки

### Ошибка: "Already on 'branch-name'"

\`\`\`bash
$ git checkout feature
Already on 'feature'
\`\`\`

**Причина**: Вы уже находитесь на этой ветке
**Решение**: Ничего делать не нужно

### Ошибка: "Pathspec 'branch' did not match"

\`\`\`bash
$ git checkout feature
error: pathspec 'feature' did not match any file(s) known to git
\`\`\`

**Причина**: Ветки не существует
**Решение**: Проверьте \`git branch -a\` или создайте ветку

### Ошибка: "Cannot delete branch"

\`\`\`bash
$ git branch -d feature
error: Cannot delete branch 'feature' checked out at '/path/to/repo'
\`\`\`

**Причина**: Вы находитесь на ветке, которую пытаетесь удалить
**Решение**: Сначала переключитесь на другую ветку

### Ошибка: "Uncommitted changes"

\`\`\`bash
$ git checkout feature
error: Your local changes would be overwritten by checkout
\`\`\`

**Причина**: Незакоммиченные изменения конфликтуют с целевой веткой
**Решение**: Закоммитьте или спрячьте изменения (\`git stash\`)

## Соглашения об именах веток

### Популярные префиксы:

| Префикс | Назначение | Пример |
|---------|------------|--------|
| \`feature/\` | Новая функция | \`feature/user-auth\` |
| \`bugfix/\` | Исправление бага | \`bugfix/login-error\` |
| \`hotfix/\` | Срочное исправление | \`hotfix/security-patch\` |
| \`release/\` | Подготовка релиза | \`release/v1.0.0\` |
| \`docs/\` | Документация | \`docs/api-reference\` |
| \`refactor/\` | Рефакторинг | \`refactor/auth-module\` |
| \`test/\` | Тесты | \`test/integration-tests\` |

### Примеры хороших имён:

\`\`\`
feature/add-user-authentication
bugfix/fix-login-validation
hotfix/critical-security-vulnerability
release/v2.1.0
docs/update-readme
\`\`\`

### Примеры плохих имён:

\`\`\`
my-branch           # Непонятно, что делает
test               # Слишком общее
fix                # Что исправляет?
ivan-work          # Имя разработчика не информативно
\`\`\`

## Связь с другими концепциями

- **Merge** — объединение веток
- **Rebase** — перемещение ветки на новую базу
- **Cherry-pick** — выборочное применение коммитов
- **Remote branches** — ветки на удалённом сервере
- **Git flow** — стратегия работы с ветками
`,
      steps: [
        {
          id: 1,
          title: 'Создание новой ветки',
          description: 'Создайте ветку с именем "feature-1".',
          expectedCommand: 'git branch feature-1',
          validationRegex: 'git branch\\s+\\S+',
          hint: {
            level1: 'Вам нужно создать новую ветку для вашей функции.',
            level2: 'Используйте git branch с именем ветки.',
            level3: 'Введите: git branch feature-1',
          },
        },
        {
          id: 2,
          title: 'Список всех веток',
          description: 'Посмотрите все ветки в репозитории.',
          expectedCommand: 'git branch',
          hint: {
            level1: 'Вы хотите увидеть, какие ветки существуют.',
            level2: 'Используйте git branch без аргументов.',
            level3: 'Введите: git branch',
          },
        },
      ],
      keyInsight: {
        main: 'Ветки — это лёгкие указатели. Создавайте их свободно для экспериментов и функций!',
        example:
          'Ветка в Git — это просто файл с 40-символьным SHA-1 хешем коммита. Создание ветки занимает миллисекунды, независимо от размера проекта. Создавайте ветку для каждой задачи!',
        warning:
          'Не работайте долго в одной ветке! Чем дольше ветка существует, тем больше конфликтов возникнет при слиянии. Старайтесь сливать ветку в течение 1-2 дней.',
        tip: 'Используйте понятные имена веток: feature/user-auth, bugfix/login-error, hotfix/security-patch. Это упрощает навигацию и понимание назначения ветки.',
        additional: [
          'git branch --list "feature/*" фильтрует ветки по шаблону.',
          'git branch -m old-name new-name переименовывает ветку.',
          'git branch -d удаляет только слитую ветку, -D — принудительно.',
        ],
      },
      estimatedTime: 15,
      quizQuestions: [
        {
          id: '3-1-q1',
          question: 'Что такое ветка в Git технически?',
          options: [
            {
              id: 'a',
              text: 'Копия всех файлов проекта',
              isCorrect: false,
              explanation: 'Неправильно. В Git ветка — это не копия файлов.',
            },
            {
              id: 'b',
              text: 'Указатель на коммит (файл с хешем)',
              isCorrect: true,
              explanation: 'Правильно! Ветка — это файл в .git/refs/heads/ с SHA-1 хешем коммита.',
            },
            {
              id: 'c',
              text: 'Отдельная директория для файлов',
              isCorrect: false,
              explanation: 'Неправильно. Ветки не создают отдельные директории.',
            },
            {
              id: 'd',
              text: 'Список изменений',
              isCorrect: false,
              explanation: 'Неправильно. Ветка — это не список изменений.',
            },
          ],
          explanation:
            'Ветка в Git — это легковесный указатель на коммит. Технически это файл в .git/refs/heads/, содержащий 40-символьный SHA-1 хеш коммита.',
          difficulty: 'easy',
        },
        {
          id: '3-1-q2',
          question: 'Какая команда создаёт ветку и сразу переключается на неё?',
          options: [
            {
              id: 'a',
              text: 'git branch new-branch',
              isCorrect: false,
              explanation: 'Неправильно. Это создаёт ветку, но не переключается.',
            },
            {
              id: 'b',
              text: 'git checkout -b new-branch',
              isCorrect: true,
              explanation: 'Правильно! checkout -b создаёт и переключается.',
            },
            {
              id: 'c',
              text: 'git new-branch',
              isCorrect: false,
              explanation: 'Неправильно. Такой команды нет.',
            },
            {
              id: 'd',
              text: 'git create new-branch',
              isCorrect: false,
              explanation: 'Неправильно. Такой команды нет.',
            },
          ],
          explanation:
            'git checkout -b new-branch создаёт новую ветку и переключается на неё. В Git 2.23+ также можно использовать git switch -c new-branch.',
          difficulty: 'easy',
        },
        {
          id: '3-1-q3',
          question: 'Как удалить ветку, которая ещё не слита?',
          options: [
            {
              id: 'a',
              text: 'git branch -d branch-name',
              isCorrect: false,
              explanation: 'Неправильно. -d защищает от удаления неслитых веток.',
            },
            {
              id: 'b',
              text: 'git branch -D branch-name',
              isCorrect: true,
              explanation: 'Правильно! -D принудительно удаляет даже неслитую ветку.',
            },
            {
              id: 'c',
              text: 'git branch --delete branch-name',
              isCorrect: false,
              explanation: 'Неправильно. Это аналог -d, защищает от удаления неслитых.',
            },
            {
              id: 'd',
              text: 'git rm branch-name',
              isCorrect: false,
              explanation: 'Неправильно. git rm удаляет файлы, не ветки.',
            },
          ],
          explanation:
            'git branch -D branch-name принудительно удаляет ветку, даже если она не слита. Будьте осторожны — можно потерять несохранённую работу!',
          difficulty: 'medium',
        },
        {
          id: '3-1-q4',
          question: 'Где хранятся локальные ветки в файловой системе?',
          options: [
            {
              id: 'a',
              text: '.git/branches/',
              isCorrect: false,
              explanation: 'Неправильно. Такой директории обычно нет.',
            },
            {
              id: 'b',
              text: '.git/refs/heads/',
              isCorrect: true,
              explanation: 'Правильно! Каждая ветка — это файл в этой директории.',
            },
            {
              id: 'c',
              text: '.git/objects/',
              isCorrect: false,
              explanation: 'Неправильно. Там хранятся объекты (commits, blobs, trees).',
            },
            {
              id: 'd',
              text: '.git/logs/',
              isCorrect: false,
              explanation: 'Неправильно. Там хранится reflog.',
            },
          ],
          explanation:
            'Локальные ветки хранятся в .git/refs/heads/. Каждый файл содержит SHA-1 хеш коммита, на который указывает ветка.',
          difficulty: 'medium',
        },
      ],
      resources: [
        {
          title: 'Git Documentation: git branch',
          url: 'https://git-scm.com/docs/git-branch',
          type: 'documentation',
          description: 'Официальная документация о работе с ветками',
        },
        {
          title: 'Learn Git Branching',
          url: 'https://learngitbranching.js.org/',
          type: 'tool',
          description: 'Интерактивный визуальный туториал по ветвлению',
        },
        {
          title: 'Git Branching Explained',
          url: 'https://www.youtube.com/watch?v=e2IbNHi4uCI',
          type: 'video',
          description: 'Видео о ветвлении в Git',
        },
      ],
    },
    {
      id: '3-2',
      moduleId: 'module-3',
      title: 'Переключение веток',
      slug: 'switching-branches',
      order: 2,
      theory: `# Переключение веток

Переключайтесь между ветками для работы над разными функциями.

## История команд переключения

### Эволюция синтаксиса:

| Версия Git | Команда | Особенности |
|------------|---------|-------------|
| Все версии | \`git checkout\` | Перегруженная команда |
| Git 2.23+ | \`git switch\` | Специализированная команда |

### Почему появился git switch?

Команда \`git checkout\` делает слишком много:
- Переключает ветки
- Восстанавливает файлы
- Создаёт ветки
- Отменяет изменения

Это путает новичков. В Git 2.23 (2019) команды разделили:
- \`git switch\` — переключение веток
- \`git restore\` — восстановление файлов

## Checkout vs Switch

\`\`\`bash
# Старый способ (всё ещё работает)
git checkout branch-name

# Новый способ (Git 2.23+)
git switch branch-name
\`\`\`

### Преимущества git switch:

\`\`\`bash
# Более понятные флаги
git switch -c new-branch      # Создать и переключиться
git switch -C new-branch      # Создать (или сбросить) и переключиться

# Переключиться на предыдущую ветку
git switch -                  # Аналогично cd -

# Переключиться по шаблону
git switch -c feature/123     # Создать ветку feature/123
\`\`\`

## Создание и переключение

\`\`\`bash
# Создать и переключиться одной командой
git checkout -b new-branch

# Или с switch
git switch -c new-branch

# Создать ветку от определённого коммита
git switch -c new-branch a1b2c3d

# Создать ветку от удалённой
git switch -c feature origin/feature
\`\`\`

## Глубокое погружение: Что происходит при переключении

### Три шага переключения:

\`\`\`
1. Обновление HEAD
   .git/HEAD: ref: refs/heads/main → ref: refs/heads/feature

2. Обновление индекса (Staging Area)
   .git/index обновляется согласно дереву коммита feature

3. Обновление рабочей директории
   Файлы на диске заменяются файлами из коммита feature
\`\`\`

### Визуализация процесса:

\`\`\`
ДО переключения:
┌─────────────────────────────────────────────────────┐
│ HEAD → main (коммит A)                               │
│ Рабочая директория: файлы из коммита A               │
│ Индекс: состояние коммита A                          │
└─────────────────────────────────────────────────────┘

ПОСЛЕ git switch feature:
┌─────────────────────────────────────────────────────┐
│ HEAD → feature (коммит B)                            │
│ Рабочая директория: файлы из коммита B               │
│ Индекс: состояние коммита B                          │
└─────────────────────────────────────────────────────┘
\`\`\`

### Что Git проверяет перед переключением:

1. **Есть ли незакоммиченные изменения?**
   - Если нет — переключение безопасно
   - Если есть — Git проверит конфликты

2. **Конфликтуют ли изменения?**
   - Изменения в файлах, которые отличаются между ветками → ошибка
   - Изменения в файлах, которые одинаковые → переключение с изменениями

3. **Существует ли целевая ветка?**
   - Да — переключение
   - Нет — ошибка

## Переключение с незакоммиченными изменениями

### Сценарий 1: Изменения в файлах, которые не менялись между ветками

\`\`\`bash
# Изменили README.md, который одинаков в main и feature
git switch feature
# Switched to branch 'feature'
# Изменения в README.md сохранены!
\`\`\`

### Сценарий 2: Изменения в файлах, которые отличаются между ветками

\`\`\`bash
# Изменили src/auth.ts, который разный в main и feature
git switch feature
# error: Your local changes to the following files would be overwritten by checkout:
#   src/auth.ts
# Please commit your changes or stash them before you switch branches.
# Aborting
\`\`\`

### Решения:

\`\`\`bash
# Вариант 1: Закоммитить
git add .
git commit -m "Сохранить изменения"
git switch feature

# Вариант 2: Спрятать (stash)
git stash push -m "Временные изменения"
git switch feature
# ... поработать в feature ...
git switch main
git stash pop

# Вариант 3: Принудительно (осторожно!)
git checkout -f feature  # Изменения потеряны!
\`\`\`

## Git Stash: Спрятать изменения

### Базовое использование:

\`\`\`bash
# Спрятать текущие изменения
git stash

# С описанием
git stash push -m "Описание изменений"

# Посмотреть список спрятанных изменений
git stash list

# Пример вывода:
# stash@{0}: On main: Описание изменений
# stash@{1}: WIP on feature: a1b2c3d Последний коммит

# Применить последнее спрятанное
git stash pop

# Применить конкретное
git stash apply stash@{1}

# Удалить спрятанное
git stash drop stash@{0}

# Удалить всё
git stash clear
\`\`\`

### Продвинутые опции stash:

\`\`\`bash
# Включить неотслеживаемые файлы
git stash -u

# Включить только индексированные изменения
git stash --staged

# Оставить изменения в индексе после stash
git stash --keep-index

# Посмотреть содержимое stash
git stash show -p stash@{0}

# Создать ветку из stash
git stash branch new-branch stash@{0}
\`\`\`

### Stash workflow:

\`\`\`bash
# Сценарий: срочный багфикс во время работы над функцией

# 1. Спрятать текущую работу
git stash push -m "WIP: функция аутентификации"

# 2. Переключиться на main и создать ветку для багфикса
git switch main
git switch -c hotfix/critical-bug

# 3. Исправить баг
git add .
git commit -m "Исправить критический баг"
git switch main
git merge hotfix/critical-bug

# 4. Вернуться к работе над функцией
git switch feature/auth
git stash pop
\`\`\`

## Практические кейсы

### Кейс 1: "Срочный багфикс во время работы над функцией"

\`\`\`bash
# Работаете над функцией
git status
# modified: src/auth.ts (незавершённые изменения)

# Приходит срочный багфикс
git stash push -m "WIP: аутентификация"
git switch main
git switch -c hotfix/issue-123

# Исправляете баг
git add .
git commit -m "fix: исправить критический баг"

# Вливаете в main
git switch main
git merge hotfix/issue-123

# Возвращаетесь к функции
git switch feature/auth
git stash pop
\`\`\`

### Кейс 2: "Переключение между несколькими ветками"

\`\`\`bash
# Быстрое переключение между ветками
git switch feature/auth      # Работа над аутентификацией
git switch feature/api       # Работа над API
git switch -                 # Вернуться на feature/auth

# Посмотреть, где были
git reflog | grep checkout
\`\`\`

### Кейс 3: "Потерял изменения после переключения"

\`\`\`bash
# Случайно переключился с незакоммиченными изменениями
git switch other-branch
# Изменения потеряны!

# Проверить reflog
git reflog

# Найти коммит или stash
git stash list

# Восстановить из stash
git stash pop
\`\`\`

### Кейс 4: "Переключение на конкретный коммит (detached HEAD)"

\`\`\`bash
# Переключиться на конкретный коммит
git switch a1b2c3d
# HEAD is now at a1b2c3d...

# Это состояние "detached HEAD"
# Вы не на ветке, а на конкретном коммите

# Создать ветку из этого состояния
git switch -c new-branch

# Вернуться на ветку
git switch main
\`\`\`

## Detached HEAD

### Что это такое?

\`\`\`
Обычное состояние:
HEAD → refs/heads/main → коммит A

Detached HEAD:
HEAD → коммит A (напрямую, без ветки)
\`\`\`

### Когда возникает:

\`\`\`bash
# Переключение на коммит
git switch a1b2c3d

# Переключение на тег
git switch v1.0.0

# Переключение на удалённую ветку без создания локальной
git switch origin/main
\`\`\`

### Опасности:

\`\`\`bash
# В detached HEAD можно делать коммиты
git commit -m "Изменение"

# Но при переключении на ветку эти коммиты будут потеряны!
git switch main
# Коммиты в "воздухе" будут удалены через 90 дней

# Решение: создать ветку
git switch -c my-branch
\`\`\`

## Типичные ошибки

### Ошибка: "Your local changes would be overwritten"

\`\`\`bash
$ git switch feature
error: Your local changes to the following files would be overwritten by checkout:
  src/auth.ts
Please commit your changes or stash them before you switch branches.
\`\`\`

**Причина**: Изменения в файлах конфликтуют с целевой веткой
**Решение**:
\`\`\`bash
git stash push -m "Временные изменения"
git switch feature
\`\`\`

### Ошибка: "Already on 'branch-name'"

\`\`\`bash
$ git switch main
Already on 'main'
\`\`\`

**Причина**: Вы уже на этой ветке
**Решение**: Ничего делать не нужно

### Ошибка: "pathspec 'branch' did not match"

\`\`\`bash
$ git switch feature
fatal: invalid reference: feature
\`\`\`

**Причина**: Ветки не существует
**Решение**: Проверьте \`git branch -a\` или создайте ветку

### Ошибка: "Cannot switch branches with uncommitted submodules"

\`\`\`bash
$ git switch feature
fatal: cannot switch branches with uncommitted submodules
\`\`\`

**Причина**: Изменения в субмодулях
**Решение**: Закоммитить или спрятать изменения в субмодулях

## Полезные советы

### Быстрое переключение:

\`\`\`bash
# На предыдущую ветку
git switch -

# На ветку по частичному имени (с настройкой)
git switch -c feature/123  # Создать
git switch feature/123     # Переключиться
\`\`\`

### Проверка перед переключением:

\`\`\`bash
# Посмотреть изменения
git status
git diff

# Если есть важные изменения — закоммитить или спрятать
git stash push -m "Описание"
\`\`\`

### Алиасы для удобства:

\`\`\`bash
# Добавить в ~/.gitconfig
[alias]
  sw = switch
  swc = switch -c
  st = stash
  stp = stash pop
  stl = stash list
\`\`\`

## Связь с другими концепциями

- **git stash** — спрятать изменения перед переключением
- **git branch** — создать ветку для переключения
- **git checkout** — старая команда для переключения
- **git restore** — восстановить файлы (отдельно от переключения)
- **git reflog** — история переключений
`,
      steps: [
        {
          id: 1,
          title: 'Переключение на ветку feature',
          description: 'Переключитесь на ветку "feature-1", которую вы создали.',
          expectedCommand: 'git checkout feature-1',
          validationRegex: 'git (checkout|switch)\\s+feature-1',
          hint: {
            level1: 'Вам нужно начать работу над веткой функции.',
            level2: 'Используйте git checkout или git switch с именем ветки.',
            level3: 'Введите: git checkout feature-1',
          },
        },
      ],
      keyInsight: {
        main: 'Всегда коммитьте или прячьте (stash) изменения перед переключением веток, чтобы избежать конфликтов.',
        example:
          'Вы работаете над feature веткой и срочно нужно исправить баг в main. Выполните git stash, переключитесь на main, исправьте баг, вернитесь на feature и выполните git stash pop.',
        warning:
          'Незакоммиченные изменения могут быть потеряны при переключении веток! Git попытается их сохранить, но конфликты могут привести к потере данных.',
        tip: 'git stash push -m "описание" сохраняет изменения с описанием. git stash list покажет все спрятанные изменения. git stash pop применит и удалит последнее.',
        additional: [
          'git stash включает изменения в отслеживаемых файлах.',
          'git stash -u включает также неотслеживаемые файлы.',
          'git stash --staged сохраняет только индексированные изменения.',
        ],
      },
      estimatedTime: 10,
      quizQuestions: [
        {
          id: '3-2-q1',
          question: 'Какая новая команда для переключения веток появилась в Git 2.23?',
          options: [
            {
              id: 'a',
              text: 'git change',
              isCorrect: false,
              explanation: 'Неправильно. Такой команды нет.',
            },
            {
              id: 'b',
              text: 'git switch',
              isCorrect: true,
              explanation:
                'Правильно! git switch — специализированная команда для переключения веток.',
            },
            {
              id: 'c',
              text: 'git goto',
              isCorrect: false,
              explanation: 'Неправильно. Такой команды нет.',
            },
            {
              id: 'd',
              text: 'git move',
              isCorrect: false,
              explanation: 'Неправильно. Такой команды нет.',
            },
          ],
          explanation:
            'В Git 2.23 (2019) команда checkout была разделена на switch (переключение веток) и restore (восстановление файлов) для ясности.',
          difficulty: 'easy',
        },
        {
          id: '3-2-q2',
          question: 'Что такое "detached HEAD"?',
          options: [
            {
              id: 'a',
              text: 'Ошибка при переключении веток',
              isCorrect: false,
              explanation: 'Неправильно. Это не ошибка, а нормальное состояние.',
            },
            {
              id: 'b',
              text: 'Состояние, когда HEAD указывает на коммит, а не на ветку',
              isCorrect: true,
              explanation: 'Правильно! HEAD указывает напрямую на коммит, а не через ветку.',
            },
            {
              id: 'c',
              text: 'Удалённая ветка без локальной копии',
              isCorrect: false,
              explanation: 'Неправильно. Это другое понятие.',
            },
            {
              id: 'd',
              text: 'Ветка без коммитов',
              isCorrect: false,
              explanation: 'Неправильно. Такое состояние невозможно.',
            },
          ],
          explanation:
            'Detached HEAD возникает, когда HEAD указывает напрямую на коммит (например, при checkout по хешу или тегу), а не на ветку. Коммиты в этом состоянии могут быть потеряны.',
          difficulty: 'medium',
        },
        {
          id: '3-2-q3',
          question: 'Как спрятать текущие изменения перед переключением ветки?',
          options: [
            {
              id: 'a',
              text: 'git hide',
              isCorrect: false,
              explanation: 'Неправильно. Такой команды нет.',
            },
            {
              id: 'b',
              text: 'git stash',
              isCorrect: true,
              explanation: 'Правильно! git stash сохраняет изменения во временном хранилище.',
            },
            {
              id: 'c',
              text: 'git save',
              isCorrect: false,
              explanation: 'Неправильно. Такой команды нет.',
            },
            {
              id: 'd',
              text: 'git store',
              isCorrect: false,
              explanation: 'Неправильно. Такой команды нет.',
            },
          ],
          explanation:
            'git stash сохраняет изменения из рабочей директории и индекса во временное хранилище. git stash pop возвращает изменения.',
          difficulty: 'easy',
        },
        {
          id: '3-2-q4',
          question: 'Как переключиться на предыдущую ветку?',
          options: [
            {
              id: 'a',
              text: 'git switch back',
              isCorrect: false,
              explanation: 'Неправильно. Такой команды нет.',
            },
            {
              id: 'b',
              text: 'git switch -',
              isCorrect: true,
              explanation:
                'Правильно! Знак - работает как в cd - для перехода в предыдущую директорию.',
            },
            {
              id: 'c',
              text: 'git switch previous',
              isCorrect: false,
              explanation: 'Неправильно. Такой команды нет.',
            },
            {
              id: 'd',
              text: 'git switch last',
              isCorrect: false,
              explanation: 'Неправильно. Такой команды нет.',
            },
          ],
          explanation:
            'git switch - (или git checkout -) переключает на предыдущую ветку. Это аналогично cd - в shell для перехода в предыдущую директорию.',
          difficulty: 'easy',
        },
      ],
      resources: [
        {
          title: 'Git Documentation: git checkout',
          url: 'https://git-scm.com/docs/git-checkout',
          type: 'documentation',
          description: 'Официальная документация о команде checkout',
        },
        {
          title: 'Git Switch Documentation',
          url: 'https://git-scm.com/docs/git-switch',
          type: 'documentation',
          description: 'Новая команда git switch для переключения веток',
        },
        {
          title: 'Git Stash Explained',
          url: 'https://www.atlassian.com/git/tutorials/saving-changes/git-stash',
          type: 'article',
          description: 'Подробное руководство по git stash от Atlassian',
        },
      ],
    },
    {
      id: '3-3',
      moduleId: 'module-3',
      title: 'Слияние веток',
      slug: 'merging-branches',
      order: 3,
      theory: `# Слияние веток

Слияние объединяет изменения из разных веток.

:::diagram branch-merge:::

## История концепции слияния

### Эволюция подходов к объединению изменений:

| Система | Подход | Проблема |
|---------|--------|----------|
| CVS | Ручное слияние | Сложно, конфликтно |
| Subversion | svn merge | Медленно, централизованно |
| **Git** | **Автоматическое слияние** | **Умное, быстрое** |

### Почему слияние в Git особенное?

Git использует **трёхстороннее слияние** (three-way merge):
- Находит общего предка двух веток
- Сравнивает изменения в каждой ветке
- Автоматически объединяет неконфликтующие изменения

Это делает слияние быстрым и предсказуемым.

## Типы слияний

### 1. Fast-forward (перемотка)

Происходит, когда целевая ветка не расходилась с исходной.

\`\`\`
ДО слияния:
main:    A --- B
                \\
feature:         C --- D

ПОСЛЕ git merge feature (fast-forward):
main:    A --- B --- C --- D
                \\
feature:         C --- D
\`\`\`

\`\`\`bash
git merge feature-branch
# Git просто перемещает указатель main на D
\`\`\`

### 2. Three-way merge (трёхстороннее слияние)

Происходит, когда ветки разошлись.

\`\`\`
ДО слияния:
main:    A --- B --- C
                \\
feature:         D --- E

ПОСЛЕ git merge feature:
main:    A --- B --- C --- M (merge commit)
                \\         /
feature:         D --- E
\`\`\`

\`\`\`bash
git merge feature-branch
# Git создаёт новый коммит слияния M с двумя родителями
\`\`\`

## Глубокое погружение: Как работает слияние

### Трёхстороннее слияние:

\`\`\`
          Общий предок (base)
                 A
                / \\
               /   \\
          B (main)  C (feature)
              \\     /
               \\   /
                M (merge commit)
\`\`\`

**Алгоритм:**
1. Найти общего предка (merge base)
2. Вычислить diff между base и main
3. Вычислить diff между base и feature
4. Объединить изменения автоматически
5. Создать merge commit (если нужно)

### Что происходит внутри:

\`\`\`bash
# Найти общего предка
git merge-base main feature
# a1b2c3d (хеш общего предка)

# Git выполняет:
# 1. git diff a1b2c3d main → изменения в main
# 2. git diff a1b2c3d feature → изменения в feature
# 3. Объединяет изменения
# 4. Создаёт merge commit (если нужно)
\`\`\`

### Структура merge commit:

\`\`\`bash
$ git cat-file -p <merge-commit>
tree t1e2s3t...
parent a1b2c3d...  # Первый родитель (main)
parent e4f5g6h...  # Второй родитель (feature)
author Иван <ivan@mail.ru> 1234567890 +0300
committer Иван <ivan@mail.ru> 1234567890 +0300

Merge branch 'feature' into main
\`\`\`

Merge commit имеет **двух родителей**!

## Стратегии слияния

### 1. Fast-forward only

\`\`\`bash
# Только fast-forward, иначе ошибка
git merge --ff-only feature
\`\`\`

Используется, когда нужно сохранить линейную историю.

### 2. No fast-forward

\`\`\`bash
# Всегда создавать merge commit
git merge --no-ff feature
\`\`\`

Используется для сохранения истории ветвления.

### 3. Squash

\`\`\`bash
# "Сплющить" все коммиты ветки в один
git merge --squash feature
git commit -m "Добавить функцию аутентификации"
\`\`\`

Используется для чистой истории без merge commits.

### Сравнение стратегий:

| Стратегия | Merge commit | История ветки | Когда использовать |
|-----------|--------------|---------------|-------------------|
| ff-only | Нет | Теряется | Линейная история |
| no-ff | Да | Сохраняется | Feature ветки |
| squash | Нет | Теряется | Маленькие PR |

### Визуальное сравнение:

\`\`\`
Fast-forward:
A --- B --- C --- D (линейная история)

No-ff:
A --- B --- C --- M
            \\     /
             D --- E (видно ветвление)

Squash:
A --- B --- C --- S (один коммит вместо D и E)
\`\`\`

## Процесс слияния

### Шаг 1: Подготовка

\`\`\`bash
# Убедиться, что рабочая директория чиста
git status

# Переключиться на целевую ветку
git checkout main

# Получить последние изменения
git pull origin main
\`\`\`

### Шаг 2: Слияние

\`\`\`bash
# Слить ветку
git merge feature-branch

# Или с опциями
git merge --no-ff feature-branch
git merge --squash feature-branch
\`\`\`

### Шаг 3: Разрешение конфликтов (если есть)

\`\`\`bash
# Посмотреть конфликтующие файлы
git status

# Разрешить конфликты вручную или
git mergetool

# Добавить разрешённые файлы
git add .

# Завершить слияние
git commit
\`\`\`

### Шаг 4: Отправка

\`\`\`bash
# Отправить изменения
git push origin main
\`\`\`

## Отмена слияния

### Отмена в процессе (до завершения):

\`\`\`bash
# Если возникли проблемы
git merge --abort

# Вернуться к состоянию до слияния
\`\`\`

### Отмена после завершения:

\`\`\`bash
# Отменить последний merge commit
git reset --hard HEAD~1

# Или более безопасно
git revert -m 1 HEAD
# -m 1 означает "оставить изменения первого родителя (main)"
\`\`\`

### Разница между reset и revert:

\`\`\`bash
# reset — переписывает историю
git reset --hard HEAD~1
# Опасно для публичных веток!

# revert — создаёт новый коммит
git revert -m 1 HEAD
# Безопасно для публичных веток
\`\`\`

## Продвинутые опции

### Сообщение слияния:

\`\`\`bash
# Кастомное сообщение
git merge -m "Слить функцию аутентификации" feature

# Редактировать сообщение в редакторе
git merge --edit feature

# Добавить детали в стандартное сообщение
git merge feature
# Откроется редактор с шаблоном
\`\`\`

### Подписание merge commit:

\`\`\`bash
# Подписать GPG
git merge -S feature

# Проверить подпись
git verify-commit HEAD
\`\`\`

### Слияние с проверкой:

\`\`\`bash
# Не коммитить, только проверить
git merge --no-commit feature

# Посмотреть, что будет
git diff --cached

# Если всё ок — завершить
git commit

# Или отменить
git merge --abort
\`\`\`

## Практические кейсы

### Кейс 1: "Слияние feature ветки с --no-ff"

\`\`\`bash
# Feature ветка готова к слиянию
git checkout main
git pull origin main
git merge --no-ff feature/user-auth

# Откроется редактор для сообщения
# Сохранить стандартное или отредактировать

# Отправить
git push origin main

# Удалить слитую ветку
git branch -d feature/user-auth
git push origin --delete feature/user-auth
\`\`\`

### Кейс 2: "Слияние с конфликтом"

\`\`\`bash
git merge feature/api
# CONFLICT (content): Merge conflict in src/api.ts
# Automatic merge failed; fix conflicts and then commit the result.

# Посмотреть конфликты
git status
# Unmerged paths:
#   both modified: src/api.ts

# Разрешить конфликт (отредактировать файл)
# Удалить маркеры <<<<<<< ======= >>>>>>>

# Добавить разрешённый файл
git add src/api.ts

# Завершить слияние
git commit
\`\`\`

### Кейс 3: "Отмена неудачного слияния"

\`\`\`bash
# Слияние прошло, но обнаружились проблемы
git merge feature/broken

# Тесты падают!

# Вариант 1: reset (если ещё не push)
git reset --hard HEAD~1

# Вариант 2: revert (если уже push)
git revert -m 1 HEAD
git push origin main
\`\`\`

### Кейс 4: "Squash merge для чистой истории"

\`\`\`bash
# Feature ветка с множеством мелких коммитов
git log --oneline feature/cleanup
# a1b2c3d Исправить опечатку
# e4f5g6h Добавить тесты
# i7j8k9l Рефакторинг
# m9n8o7p Ещё исправления

# Squash merge
git checkout main
git merge --squash feature/cleanup

# Один коммит вместо четырёх
git commit -m "Рефакторинг модуля аутентификации"
\`\`\`

### Кейс 5: "Слияние длинной ветки"

\`\`\`bash
# Длинная ветка, которая давно не обновлялась
git checkout long-running-feature

# Сначала обновить от main
git fetch origin
git merge origin/main

# Разрешить конфликты...

# Затем вернуться на main и слить
git checkout main
git merge long-running-feature
\`\`\`

## Типичные ошибки

### Ошибка: "Merge conflict"

\`\`\`bash
$ git merge feature
CONFLICT (content): Merge conflict in src/auth.ts
Automatic merge failed; fix conflicts and then commit the result.
\`\`\`

**Причина**: Обе ветки изменили одни и те же строки
**Решение**: Разрешить конфликт вручную

### Ошибка: "Not possible to fast-forward"

\`\`\`bash
$ git merge --ff-only feature
fatal: Not possible to fast-forward, aborting.
\`\`\`

**Причина**: Ветки разошлись, fast-forward невозможен
**Решение**: Использовать обычное слияние или rebase

### Ошибка: "You have uncommitted changes"

\`\`\`bash
$ git merge feature
error: Your local changes would be overwritten by merge.
\`\`\`

**Причина**: Незакоммиченные изменения в рабочей директории
**Решение**: Закоммитить или спрятать изменения

### Ошибка: "Merge with strategy ours failed"

\`\`\`bash
$ git merge -s ours feature
error: merge with strategy ours failed
\`\`\`

**Причина**: Стратегия "ours" требует определённых условий
**Решение**: Использовать стандартную стратегию

## Полезные советы

### Проверка перед слиянием:

\`\`\`bash
# Посмотреть, что изменится
git diff main...feature

# Посмотреть коммиты, которые будут слиты
git log main..feature --oneline

# Посмотреть файлы, которые изменятся
git diff --name-only main...feature
\`\`\`

### Слияние с тестированием:

\`\`\`bash
# Слить без коммита
git merge --no-commit feature

# Запустить тесты
npm test

# Если всё ок
git commit

# Если проблемы
git merge --abort
\`\`\`

### Алиасы для удобства:

\`\`\`bash
# Добавить в ~/.gitconfig
[alias]
  mff = merge --ff-only
  mnf = merge --no-ff
  msq = merge --squash
  ma = merge --abort
\`\`\`

## Связь с другими концепциями

- **git rebase** — альтернатива слиянию для линейной истории
- **git cherry-pick** — выборочное применение коммитов
- **git revert** — отмена merge commit
- **git rerere** — запоминание разрешения конфликтов
- **git mergetool** — визуальный инструмент для конфликтов
`,
      steps: [
        {
          id: 1,
          title: 'Переключение на main',
          description: 'Переключитесь обратно на ветку main для слияния.',
          expectedCommand: 'git checkout main',
          validationRegex: 'git (checkout|switch)\\s+main',
          hint: {
            level1: 'Вам нужно вернуться на основную ветку.',
            level2: 'Используйте git checkout или git switch с именем main.',
            level3: 'Введите: git checkout main',
          },
        },
        {
          id: 2,
          title: 'Слияние ветки feature-1',
          description: 'Слейте изменения из ветки feature-1 в main.',
          expectedCommand: 'git merge feature-1',
          hint: {
            level1: 'Объедините изменения из ветки функции.',
            level2: 'Используйте git merge с именем ветки.',
            level3: 'Введите: git merge feature-1',
          },
        },
      ],
      keyInsight: {
        main: 'Fast-forward слияние сохраняет линейную историю. Трёхстороннее слияние создаёт коммит слияния.',
        example:
          'Fast-forward: main указывает на A, feature указывает на A→B→C. После merge main просто перемещается на C. Трёхстороннее: main на A→D, feature на A→B→C. Git создаёт коммит E с родителями D и C.',
        warning:
          'Fast-forward скрывает историю ветвления — непонятно, где была feature ветка. Используйте git merge --no-ff для сохранения истории ветки.',
        tip: 'Для feature веток используйте --no-ff (сохраняет контекст). Для hotfix или маленьких изменений fast-forward приемлем.',
        additional: [
          'git log --graph визуализирует историю слияний.',
          'Коммит слияния имеет двух родителей.',
          'git merge --ff-only отменяет слияние, если fast-forward невозможен.',
        ],
      },
      estimatedTime: 15,
      quizQuestions: [
        {
          id: '3-3-q1',
          question: 'Что такое fast-forward merge?',
          options: [
            {
              id: 'a',
              text: 'Слияние с созданием нового коммита',
              isCorrect: false,
              explanation: 'Неправильно. Это трёхстороннее слияние.',
            },
            {
              id: 'b',
              text: 'Перемещение указателя ветки без создания коммита',
              isCorrect: true,
              explanation: 'Правильно! Fast-forward просто перемещает указатель ветки.',
            },
            {
              id: 'c',
              text: 'Быстрое удаление ветки',
              isCorrect: false,
              explanation: 'Неправильно. Это не связано с удалением.',
            },
            {
              id: 'd',
              text: 'Слияние с конфликтами',
              isCorrect: false,
              explanation: 'Неправильно. Fast-forward не создаёт конфликтов.',
            },
          ],
          explanation:
            'Fast-forward merge происходит, когда целевая ветка не расходилась с исходной. Git просто перемещает указатель ветки вперёд, не создавая merge commit.',
          difficulty: 'easy',
        },
        {
          id: '3-3-q2',
          question: 'Какой флаг создаёт merge commit даже при возможности fast-forward?',
          options: [
            {
              id: 'a',
              text: 'git merge --ff',
              isCorrect: false,
              explanation: 'Неправильно. --ff разрешает fast-forward (по умолчанию).',
            },
            {
              id: 'b',
              text: 'git merge --no-ff',
              isCorrect: true,
              explanation: 'Правильно! --no-ff всегда создаёт merge commit.',
            },
            {
              id: 'c',
              text: 'git merge --commit',
              isCorrect: false,
              explanation: 'Неправильно. Это не влияет на fast-forward.',
            },
            {
              id: 'd',
              text: 'git merge --force',
              isCorrect: false,
              explanation: 'Неправильно. Такого флага нет для merge.',
            },
          ],
          explanation:
            'git merge --no-ff (no fast-forward) всегда создаёт merge commit, даже если возможен fast-forward. Это сохраняет историю ветвления.',
          difficulty: 'easy',
        },
        {
          id: '3-3-q3',
          question: 'Сколько родителей имеет merge commit?',
          options: [
            {
              id: 'a',
              text: 'Один',
              isCorrect: false,
              explanation: 'Неправильно. Обычный коммит имеет одного родителя.',
            },
            {
              id: 'b',
              text: 'Два',
              isCorrect: true,
              explanation:
                'Правильно! Merge commit имеет двух родителей — по одному от каждой ветки.',
            },
            {
              id: 'c',
              text: 'Три',
              isCorrect: false,
              explanation: 'Неправильно. Merge commit имеет двух родителей.',
            },
            {
              id: 'd',
              text: 'Зависит от количества веток',
              isCorrect: false,
              explanation: 'Неправильно. Обычный merge имеет двух родителей.',
            },
          ],
          explanation:
            'Merge commit имеет двух родителей: первый — коммит из целевой ветки (куда сливаем), второй — из исходной ветки (что сливаем).',
          difficulty: 'medium',
        },
        {
          id: '3-3-q4',
          question: 'Как отменить слияние в процессе (до завершения)?',
          options: [
            {
              id: 'a',
              text: 'git merge --cancel',
              isCorrect: false,
              explanation: 'Неправильно. Такого флага нет.',
            },
            {
              id: 'b',
              text: 'git merge --abort',
              isCorrect: true,
              explanation:
                'Правильно! --abort отменяет слияние и возвращает к исходному состоянию.',
            },
            {
              id: 'c',
              text: 'git merge --undo',
              isCorrect: false,
              explanation: 'Неправильно. Такого флага нет.',
            },
            {
              id: 'd',
              text: 'git merge --reset',
              isCorrect: false,
              explanation: 'Неправильно. Такого флага нет.',
            },
          ],
          explanation:
            'git merge --abort отменяет текущее слияние и возвращает репозиторий к состоянию до начала слияния. Это работает только во время незавершённого слияния.',
          difficulty: 'easy',
        },
      ],
      resources: [
        {
          title: 'Git Documentation: git merge',
          url: 'https://git-scm.com/docs/git-merge',
          type: 'documentation',
          description: 'Официальная документация о слиянии веток',
        },
        {
          title: 'Git Merge Strategies',
          url: 'https://www.atlassian.com/git/tutorials/using-branches/git-merge',
          type: 'article',
          description: 'Статья о стратегиях слияния в Git',
        },
        {
          title: 'Fast-Forward vs Merge Commit',
          url: 'https://www.youtube.com/watch?v=0iuqXh0ooL4',
          type: 'video',
          description: 'Видео о типах слияния',
        },
      ],
    },
    {
      id: '3-4',
      moduleId: 'module-3',
      title: 'Конфликты слияния',
      slug: 'merge-conflicts',
      order: 4,
      theory: `# Конфликты слияния

Конфликты возникают, когда Git не может автоматически объединить изменения.

## История концепции разрешения конфликтов

### Эволюция подходов:

| Система | Подход | Проблема |
|---------|--------|----------|
| CVS | Ручное разрешение | Сложно, много ошибок |
| Subversion | svn resolved | Централизованно |
| **Git** | **Автоматическое + ручное** | **Гибко, локально** |

### Почему конфликты неизбежны?

В любой командной разработке:
- Несколько разработчиков работают над одним кодом
- Изменения в одних и тех же файлах
- Разные подходы к решению задач

Git делает всё возможное для автоматического слияния, но:
- **Неконфликтующие изменения** → сливаются автоматически
- **Конфликтующие изменения** → требуют ручного разрешения

## Когда возникают конфликты?

### Типы конфликтов:

| Тип | Описание | Пример |
|-----|----------|--------|
| **Content** | Одни и те же строки изменены | Обе ветки изменили строку 10 |
| **Delete/Modify** | Файл удалён и изменён | Ветка A удалила, B изменила |
| **Rename/Rename** | Файл переименован по-разному | A → auth.ts, B → login.ts |
| **Binary** | Конфликт в бинарных файлах | Изменены изображения |

### Примеры ситуаций:

\`\`\`bash
# Конфликт содержимого
CONFLICT (content): Merge conflict in src/auth.ts

# Конфликт удаления
CONFLICT (modify/delete): src/old-module.ts deleted in feature and modified in main

# Конфликт переименования
CONFLICT (rename/rename): src/api.ts renamed to src/rest.ts in feature and to src/http.ts in main

# Конфликт бинарного файла
CONFLICT (content): Merge conflict in assets/logo.png
\`\`\`

## Глубокое погружение: Маркеры конфликта

### Структура маркеров:

\`\`\`
<<<<<<< HEAD (текущая ветка)
Изменения в текущей ветке (где вы находитесь)
=======
Изменения в сливаемой ветке (которую сливаете)
>>>>>>> feature-branch
\`\`\`

### Подробный пример:

\`\`\`typescript
// Исходный файл (общий предок):
function greet(name: string) {
  return "Hello, " + name;
}

// Ветка main:
function greet(name: string) {
  return "Привет, " + name;  // Изменили на русский
}

// Ветка feature:
function greet(name: string) {
  return "Hello, " + name + "!";  // Добавили восклицание
}

// Результат конфликта:
function greet(name: string) {
<<<<<<< HEAD
  return "Привет, " + name;
=======
  return "Hello, " + name + "!";
>>>>>>> feature
}
\`\`\`

### Множественные конфликты в одном файле:

\`\`\`typescript
function config() {
<<<<<<< HEAD
  const apiUrl = "https://api.example.com";
=======
  const apiUrl = "https://dev.example.com/api";
>>>>>>> feature

  const timeout = 5000;
  
<<<<<<< HEAD
  const retries = 3;
=======
  const retries = 5;
>>>>>>> feature
}
\`\`\`

## Процесс разрешения конфликта

### Шаг 1: Обнаружение конфликта

\`\`\`bash
$ git merge feature
Auto-merging src/auth.ts
CONFLICT (content): Merge conflict in src/auth.ts
Automatic merge failed; fix conflicts and then commit the result.

# Посмотреть статус
$ git status
Unmerged paths:
  (use "git add <file>..." to mark resolution)
  both modified:   src/auth.ts
\`\`\`

### Шаг 2: Просмотр конфликта

\`\`\`bash
# Посмотреть файлы с конфликтами
git diff --name-only --diff-filter=U

# Посмотреть содержимое конфликта
git diff

# Посмотреть конфликт в конкретном файле
git diff src/auth.ts
\`\`\`

### Шаг 3: Разрешение

**Вариант 1: Ручное редактирование**

\`\`\`bash
# Открыть файл в редакторе
vim src/auth.ts
# или
code src/auth.ts

# Отредактировать, оставив нужную версию:
function greet(name: string) {
  return "Привет, " + name + "!";  // Комбинированное решение
}
\`\`\`

**Вариант 2: Принять одну из версий**

\`\`\`bash
# Принять версию текущей ветки (HEAD)
git checkout --ours src/auth.ts

# Принять версию сливаемой ветки
git checkout --theirs src/auth.ts

# Для конкретных файлов
git checkout --ours -- src/config.ts
git checkout --theirs -- src/api.ts
\`\`\`

**Вариант 3: Использовать mergetool**

\`\`\`bash
# Открыть визуальный инструмент
git mergetool

# Список доступных инструментов
git mergetool --tool-help

# Использовать конкретный инструмент
git mergetool --tool=vimdiff
git mergetool --tool=meld
git mergetool --tool=code
\`\`\`

### Шаг 4: Пометить как разрешённый

\`\`\`bash
# Добавить разрешённый файл
git add src/auth.ts

# Проверить, что все конфликты разрешены
git status

# Если всё готово — завершить слияние
git commit
\`\`\`

## Инструменты для разрешения конфликтов

### Популярные merge tools:

| Инструмент | Платформа | Особенности |
|------------|-----------|-------------|
| **Meld** | Linux, Windows, Mac | Бесплатный, GUI |
| **Beyond Compare** | Windows, Mac | Платный, мощный |
| **KDiff3** | Все | Бесплатный |
| **vimdiff** | Все | Встроен в Vim |
| **VS Code** | Все | Встроенная поддержка |
| **P4Merge** | Все | Бесплатный от Perforce |

### Настройка mergetool:

\`\`\`bash
# Установить инструмент по умолчанию
git config --global merge.tool meld

# Отключить создание backup файлов
git config --global mergetool.keepBackup false

# Автоматически запускать mergetool для всех конфликтов
git config --global mergetool.prompt false
\`\`\`

### VS Code для разрешения конфликт:

VS Code имеет встроенную поддержку конфликтов с кнопками:
- **Accept Current Change** — принять текущую версию
- **Accept Incoming Change** — принять входящую версию
- **Accept Both Changes** — оставить обе
- **Compare Changes** — открыть сравнение

## Продвинутые техники

### git rerere (Reuse Recorded Resolution)

Git может запоминать разрешения конфликтов:

\`\`\`bash
# Включить rerere
git config --global rerere.enabled true

# Теперь Git запомнит, как вы разрешили конфликт
# При следующем похожем конфликте — применит автоматически
\`\`\`

### Стратегии автоматического разрешения:

\`\`\`bash
# Стратегия "ours" — всегда брать текущую версию
git merge -X ours feature

# Стратегия "theirs" — всегда брать входящую версию
git merge -X theirs feature

# Игнорировать изменения в пробелах
git merge -X ignore-space-change feature
\`\`\`

## Практические кейсы

### Кейс 1: "Конфликт в импортах"

\`\`\`typescript
// main:
import { login, logout } from './auth';
import { User } from './types';

// feature:
import { login, register } from './auth';
import { Session } from './types';

// Разрешение:
import { login, logout, register } from './auth';
import { User, Session } from './types';
\`\`\`

### Кейс 2: "Массовое разрешение конфликтов"

\`\`\`bash
# Много файлов с конфликтами
git status
# Unmerged paths:
#   both modified:   src/auth.ts
#   both modified:   src/api.ts
#   both modified:   src/config.ts

# Принять все изменения из feature
git checkout --theirs .
git add .

# Или разрешить по файлам
git checkout --ours src/config.ts
git checkout --theirs src/api.ts
# src/auth.ts — вручную
\`\`\`

### Кейс 3: "Отмена слияния с конфликтами"

\`\`\`bash
# Начали слияние, но поняли, что не готовы
git merge feature
# CONFLICT...

# Отменить слияние
git merge --abort

# Вернуться к исходному состоянию
\`\`\`

### Кейс 4: "Конфликт при rebase"

\`\`\`bash
# Rebase с конфликтами
git rebase main
# CONFLICT...

# Разрешить конфликт
vim src/auth.ts
git add src/auth.ts

# Продолжить rebase
git rebase --continue

# Или пропустить этот коммит
git rebase --skip

# Или отменить rebase
git rebase --abort
\`\`\`

## Типичные ошибки

### Ошибка: "Забыл удалить маркеры конфликта"

\`\`\`bash
# Закоммитили файл с маркерами
git commit -m "Resolve conflict"

# В файле остались <<<<<<< HEAD
# При следующем merge Git будет путаться
\`\`\`

**Решение**: Всегда проверяйте файл перед \`git add\`

### Ошибка: "Принял не ту версию"

\`\`\`bash
# Случайно приняли --ours вместо --theirs
git checkout --ours important-config.ts

# Нужно отменить и разрешить заново
git checkout --merge important-config.ts
\`\`\`

### Ошибка: "Незавершённое слияние"

\`\`\`bash
$ git merge feature
CONFLICT...

$ git commit
error: Committing is not possible because you have unmerged files.
\`\`\`

**Решение**: Сначала разрешите все конфликты и \`git add\`

## Полезные советы

### Проверка перед слиянием:

\`\`\`bash
# Посмотреть потенциальные конфликты
git diff main...feature

# Посмотреть файлы, которые изменены в обеих ветках
git diff --name-only main...feature
\`\`\`

### Алиасы для разрешения конфликтов:

\`\`\`bash
# Добавить в ~/.gitconfig
[alias]
  # Показать файлы с конфликтами
  conflicts = diff --name-only --diff-filter=U
  
  # Принять текущую версию
  ours = "!f() { git checkout --ours $@ && git add $@; }; f"
  
  # Принять входящую версию
  theirs = "!f() { git checkout --theirs $@ && git add $@; }; f"
\`\`\`

## Связь с другими концепциями

- **git merge** — вызывает конфликты при слиянии
- **git rebase** — вызывает конфликты при перемещении
- **git cherry-pick** — может вызвать конфликты
- **git rerere** — запоминание разрешений
- **git mergetool** — визуальные инструменты
- **git checkout --ours/--theirs** — быстрый выбор версии
`,
      steps: [
        {
          id: 1,
          title: 'Проверка статуса',
          description: 'Проверьте текущее состояние репозитория.',
          expectedCommand: 'git status',
          hint: {
            level1: 'Проверьте состояние репозитория.',
            level2: 'Используйте git status.',
            level3: 'Введите: git status',
          },
        },
      ],
      keyInsight: {
        main: 'Конфликты — это нормально. Научитесь их разрешать спокойно и систематически.',
        example:
          'Конфликт возникает, когда обе ветки изменили одну строку. Git отмечает файл маркерами <<<<<<< HEAD, =======, >>>>>>> branch. Ваша задача — отредактировать файл, оставив правильную версию.',
        warning:
          'Не удаляйте маркеры конфликта случайно! Убедитесь, что файл корректен после разрешения. git status покажет файлы с конфликтами.',
        tip: 'Используйте git mergetool для визуального разрешения конфликтов. Или git checkout --ours/--theirs для принятия одной из версий полностью.',
        additional: [
          'git diff --check покажет маркеры конфликта.',
          'git merge --abort отменяет слияние и возвращает к исходному состоянию.',
          'Конфликты в бинарных файлах требуют ручного выбора версии.',
        ],
      },
      estimatedTime: 20,
      quizQuestions: [
        {
          id: '3-4-q1',
          question: 'Какие маркеры Git использует для обозначения конфликтов?',
          options: [
            {
              id: 'a',
              text: '<<< HEAD === >>> branch',
              isCorrect: false,
              explanation: 'Неправильно. Неполные маркеры.',
            },
            {
              id: 'b',
              text: '<<<<<<< HEAD ======= >>>>>>> branch',
              isCorrect: true,
              explanation: 'Правильно! Эти три маркера обозначают конфликтующие части.',
            },
            {
              id: 'c',
              text: '<<< OURS === THEIRS >>>',
              isCorrect: false,
              explanation: 'Неправильно. Git использует HEAD и имя ветки.',
            },
            {
              id: 'd',
              text: '[[[ HEAD ||| branch ]]]',
              isCorrect: false,
              explanation: 'Неправильно. Такой синтаксис не используется.',
            },
          ],
          explanation:
            'Git использует три маркера: <<<<<<< HEAD (начало текущей версии), ======= (разделитель), >>>>>>> branch (конец входящей версии).',
          difficulty: 'easy',
        },
        {
          id: '3-4-q2',
          question: 'Как принять версию текущей ветки при конфликте?',
          options: [
            {
              id: 'a',
              text: 'git checkout --theirs filename',
              isCorrect: false,
              explanation: 'Неправильно. --theirs принимает входящую версию.',
            },
            {
              id: 'b',
              text: 'git checkout --ours filename',
              isCorrect: true,
              explanation: 'Правильно! --ours принимает версию текущей ветки (где вы находитесь).',
            },
            {
              id: 'c',
              text: 'git checkout --current filename',
              isCorrect: false,
              explanation: 'Неправильно. Такого флага нет.',
            },
            {
              id: 'd',
              text: 'git checkout --local filename',
              isCorrect: false,
              explanation: 'Неправильно. Такого флага нет.',
            },
          ],
          explanation:
            'git checkout --ours filename принимает версию текущей ветки (HEAD). --theirs принимает версию сливаемой ветки. После этого нужно git add filename.',
          difficulty: 'medium',
        },
        {
          id: '3-4-q3',
          question: 'Как пометить файл как разрешённый после ручного редактирования?',
          options: [
            {
              id: 'a',
              text: 'git resolve filename',
              isCorrect: false,
              explanation: 'Неправильно. Такой команды нет.',
            },
            {
              id: 'b',
              text: 'git add filename',
              isCorrect: true,
              explanation: 'Правильно! git add помечает конфликт как разрешённый.',
            },
            {
              id: 'c',
              text: 'git fix filename',
              isCorrect: false,
              explanation: 'Неправильно. Такой команды нет.',
            },
            {
              id: 'd',
              text: 'git done filename',
              isCorrect: false,
              explanation: 'Неправильно. Такой команды нет.',
            },
          ],
          explanation:
            'После разрешения конфликта в файле выполните git add filename. Это помечает файл как разрешённый и готовый к коммиту.',
          difficulty: 'easy',
        },
        {
          id: '3-4-q4',
          question:
            'Какой тип конфликта возникает, когда файл удалён в одной ветке и изменён в другой?',
          options: [
            {
              id: 'a',
              text: 'Content conflict',
              isCorrect: false,
              explanation: 'Неправильно. Content conflict — это конфликт содержимого.',
            },
            {
              id: 'b',
              text: 'Modify/delete conflict',
              isCorrect: true,
              explanation: 'Правильно! Это конфликт изменения/удаления.',
            },
            {
              id: 'c',
              text: 'Rename conflict',
              isCorrect: false,
              explanation: 'Неправильно. Rename conflict — это конфликт переименования.',
            },
            {
              id: 'd',
              text: 'Binary conflict',
              isCorrect: false,
              explanation: 'Неправильно. Binary conflict — это конфликт бинарных файлов.',
            },
          ],
          explanation:
            'Modify/delete conflict возникает, когда файл изменён в одной ветке и удалён в другой. Git не может автоматически решить, удалить файл или сохранить изменения.',
          difficulty: 'medium',
        },
      ],
      resources: [
        {
          title: 'Resolving Merge Conflicts',
          url: 'https://www.atlassian.com/git/tutorials/using-branches/merge-conflicts',
          type: 'article',
          description: 'Туториал Atlassian о разрешении конфликтов',
        },
        {
          title: 'Git Merge Tools Comparison',
          url: 'https://www.gitkraken.com/learn/git/git-merge-conflicts',
          type: 'article',
          description: 'Обзор инструментов для разрешения конфликтов',
        },
        {
          title: 'Advanced Merging',
          url: 'https://git-scm.com/book/en/v2/Git-Tools-Advanced-Merging',
          type: 'book',
          description: 'Глава Pro Git о продвинутом слиянии',
        },
      ],
    },
  ],
};

export { module3 };
